import { useShallow } from 'zustand/react/shallow'
import type { Entity, EntityMap } from '@/ha/types'
import { useEntitiesStore } from './entities'

const UNAVAILABLE = new Set(['unavailable', 'unknown', 'none', ''])

/** One entity, by id. Rerenders only when that entity's object reference changes. */
export function useEntity(id: string | undefined): Entity | undefined {
  return useEntitiesStore((s) => (id ? s.entities[id] : undefined))
}

/** Just the state string — the tightest possible subscription. */
export function useEntityState(id: string | undefined): string | undefined {
  return useEntitiesStore((s) => (id ? s.entities[id]?.state : undefined))
}

/** Several entities at once; shallow-compared so unrelated ticks don't rerender. */
export function useEntities(ids: readonly string[]): (Entity | undefined)[] {
  return useEntitiesStore(useShallow((s) => ids.map((id) => s.entities[id])))
}

/** A derived value computed from the whole entity map, shallow-compared. */
export function useDerived<T>(selector: (e: EntityMap) => T): T {
  return useEntitiesStore(useShallow((s) => selector(s.entities)))
}

export function useReady(): boolean {
  return useEntitiesStore((s) => s.ready)
}

// ---- helpers on Entity (not hooks) ----

export const isAvailable = (e: Entity | undefined): boolean =>
  !!e && !UNAVAILABLE.has(e.state)

export const isOn = (e: Entity | undefined): boolean => e?.state === 'on'

export function attrNum(e: Entity | undefined, key: string): number | undefined {
  const v = e?.attributes[key]
  return typeof v === 'number' ? v : undefined
}

export function attrStr(e: Entity | undefined, key: string): string | undefined {
  const v = e?.attributes[key]
  return typeof v === 'string' ? v : undefined
}

/** Decode the handful of HTML entities HA sometimes stores in friendly names. */
function decodeEntities(s: string): string {
  return s
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
}

export function friendlyName(e: Entity | undefined, fallback = ''): string {
  const n = attrStr(e, 'friendly_name')
  return n ? decodeEntities(n) : fallback
}

/** numeric value of an entity's state, or undefined when non-numeric/unavailable. */
export function stateNum(e: Entity | undefined): number | undefined {
  if (!isAvailable(e)) return undefined
  const v = Number(e!.state)
  return Number.isFinite(v) ? v : undefined
}
